import { Suspense, useState, useEffect, useCallback } from 'react';
import type { GetStaticProps } from 'next';
import {
    getSignin,
    GetSigninResponse,
    getSimpleFooter,
    GetSimpleFooterResponse,
} from 'services/idm';
import { SigninForm } from '../../patterns';
import { useRouter } from 'next/router';
import { getAppI18nConfig, initI18n } from 'cfgi18n';
import { useTranslation } from 'react-i18next';
import { FormBox } from '../../components/form-box';
import { apiErrorHandler } from '@login/helpers';

export type staticProps = {
    footerData: GetSimpleFooterResponse;
};

export const getStaticProps = (async (params) => {
    const locale = params?.locale as string;
    const { supportedLocales } = await getAppI18nConfig('login');
    await initI18n(locale, supportedLocales);
    const footerData = (await getSimpleFooter(locale)) || [];
    return { props: { footerData, locale } };
}) satisfies GetStaticProps<staticProps>;

const SignIn = () => {
    const { i18n } = useTranslation();
    const { push } = useRouter();
    const [signinData, setSigninData] = useState<GetSigninResponse | null>(null);

    const fetchSignin = useCallback(async () => {
        const data = await getSignin(i18n.language).catch((e) => apiErrorHandler(e, i18n.language));

        if (!data) return;

        setSigninData(data);

        if (data.action === 'Redirect' && data.redirectUrl) {
            if (data.redirectUrl.startsWith('/connect')) {
                const absoluteUrl = `${window.location.origin}${data.redirectUrl}`;
                window.location.href = absoluteUrl;
                return;
            }

            push(data.redirectUrl);
        }
    }, [i18n.language, push]);

    useEffect(() => {
        fetchSignin();
    }, [fetchSignin]);

    return (
        <Suspense>
            {signinData && signinData.action === 'Display' && (
                <FormBox>
                    <SigninForm
                        placeholders={signinData.placeholders}
                        token={signinData?.xsrfToken}
                        userUnderAge={
                            signinData.action === 'Display' &&
                            signinData.flow.contentId === 'user-under-age'
                        }
                    />
                </FormBox>
            )}
        </Suspense>
    );
};

export default SignIn;
